// ===============================================================================
// LandingSimulations.js - Simulated Feature Showcases for Landing Page
// ===============================================================================
// Location: /var/www/wow/frontend/src/LandingSimulations.js
// Forest & Cream Theme - Interactive feature demonstrations
// ===============================================================================

import React from "react";
import "./LandingSimulations.css";

// ===============================================================================
// SIMULATION 1: Dashboard Preview (Hero Section)
// ===============================================================================
export function DashboardSimulation() {
  const groups = [
    { id: 1, name: "Trip to Santorini", icon: "✈️", balance: "+€125.50", positive: true, members: 4 },
    { id: 2, name: "Apartment Bills", icon: "🏠", balance: "-€45.00", positive: false, members: 3 },
    { id: 3, name: "Office Lunch", icon: "🍕", balance: "€0.00", positive: null, members: 6 },
  ];

  return (
    <div className="sim-phone">
      <div className="sim-phone-notch"></div>
      <div className="sim-phone-screen">
        {/* Header */}
        <div className="sim-app-header">
          <div className="sim-user-greeting">
            <span className="sim-wave">👋</span>
            <div>
              <p className="sim-greeting-text">Welcome back,</p>
              <h3 className="sim-user-name">Elias</h3>
            </div>
          </div>
          <div className="sim-header-actions">
            <span className="sim-icon-btn">🔔</span>
            <span className="sim-icon-btn">⚙️</span>
          </div>
        </div>

        {/* Summary Card */}
        <div className="sim-summary-card">
          <p className="sim-summary-label">Total Balance</p>
          <h2 className="sim-summary-amount">+€80.50</h2>
          <p className="sim-summary-sub">You are owed overall</p>
        </div>

        {/* Groups List */}
        <div className="sim-section">
          <div className="sim-section-header">
            <h4>Your Groups</h4>
            <button className="sim-add-btn">+ New</button>
          </div>
          <div className="sim-groups-list">
            {groups.map((group) => (
              <div key={group.id} className="sim-group-item">
                <div className="sim-group-icon">{group.icon}</div>
                <div className="sim-group-info">
                  <h5>{group.name}</h5>
                  <span className="sim-group-members">{group.members} members</span>
                </div>
                <span className={`sim-group-balance ${group.positive === true ? 'positive' : group.positive === false ? 'negative' : ''}`}>
                  {group.balance}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="sim-quick-actions">
          <button className="sim-quick-btn">
            <span>➕</span>
            <span>Add Expense</span>
          </button>
          <button className="sim-quick-btn">
            <span>💸</span>
            <span>Settle Up</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SIMULATION 2: Track Balances
// ===============================================================================
export function BalancesSimulation() {
  const balances = [
    { name: "Elias", avatar: "E", color: "#2d5a3d", amount: "+€125.50", positive: true },
    { name: "Maria", avatar: "M", color: "#5a8f6e", amount: "-€45.00", positive: false },
    { name: "Dimitroula", avatar: "D", color: "#7ab592", amount: "-€80.50", positive: false },
  ];

  const debts = [
    { from: "Maria", to: "Elias", amount: "€45.00" },
    { from: "Dimitroula", to: "Elias", amount: "€80.50" },
  ];

  return (
    <div className="sim-card-large">
      <div className="sim-card-header">
        <h3>💰 Group Balances</h3>
        <span className="sim-badge">Trip to Santorini</span>
      </div>

      {/* Balance Cards */}
      <div className="sim-balance-grid">
        {balances.map((person, idx) => (
          <div key={idx} className={`sim-balance-card ${person.positive ? 'positive' : 'negative'}`}>
            <div className="sim-balance-avatar" style={{ background: person.color }}>
              {person.avatar}
            </div>
            <div className="sim-balance-info">
              <span className="sim-balance-name">{person.name}</span>
              <span className={`sim-balance-amount ${person.positive ? 'positive' : 'negative'}`}>
                {person.amount}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Simplified Debts */}
      <div className="sim-debts-section">
        <h4>📊 Simplified Debts</h4>
        <div className="sim-debts-list">
          {debts.map((debt, idx) => (
            <div key={idx} className="sim-debt-row">
              <span className="sim-debt-from">{debt.from}</span>
              <span className="sim-debt-arrow">→ pays →</span>
              <span className="sim-debt-to">{debt.to}</span>
              <span className="sim-debt-amount">{debt.amount}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="sim-summary-bar">
        <span>✨ Only 2 payments needed to settle all debts</span>
      </div>
    </div>
  );
}

// ===============================================================================
// SIMULATION 3: Organize Expenses (Groups List)
// ===============================================================================
export function GroupsSimulation() {
  const groups = [
    { name: "Trip to Santorini", icon: "✈️", currency: "EUR", members: ["E", "M", "D", "A"], expenses: 12, total: "€1,250.00" },
    { name: "Apartment Bills", icon: "🏠", currency: "EUR", members: ["E", "M", "D"], expenses: 8, total: "€890.00" },
    { name: "Office Lunch Club", icon: "🍕", currency: "EUR", members: ["E", "M", "D", "A", "K", "S"], expenses: 24, total: "€445.50" },
    { name: "Weekend BBQ", icon: "🍖", currency: "EUR", members: ["E", "M"], expenses: 3, total: "€180.00" },
  ];

  const colors = ["#2d5a3d", "#5a8f6e", "#7ab592", "#a8d4b8", "#3d6b4f", "#4a7d5c"];

  return (
    <div className="sim-card-large">
      <div className="sim-card-header">
        <h3>📁 Your Groups</h3>
        <button className="sim-create-btn">+ Create Group</button>
      </div>

      <div className="sim-groups-grid">
        {groups.map((group, idx) => (
          <div key={idx} className="sim-group-card">
            <div className="sim-group-card-header">
              <span className="sim-group-card-icon">{group.icon}</span>
              <h4>{group.name}</h4>
            </div>
            
            <div className="sim-group-card-stats">
              <div className="sim-stat">
                <span className="sim-stat-value">{group.expenses}</span>
                <span className="sim-stat-label">expenses</span>
              </div>
              <div className="sim-stat">
                <span className="sim-stat-value">{group.total}</span>
                <span className="sim-stat-label">total</span>
              </div>
            </div>

            <div className="sim-group-card-members">
              <div className="sim-members-avatars">
                {group.members.slice(0, 4).map((m, i) => (
                  <div 
                    key={i} 
                    className="sim-mini-avatar"
                    style={{ background: colors[i % colors.length], zIndex: 10 - i }}
                  >
                    {m}
                  </div>
                ))}
                {group.members.length > 4 && (
                  <div className="sim-mini-avatar more">+{group.members.length - 4}</div>
                )}
              </div>
              <span className="sim-currency-badge">{group.currency}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===============================================================================
// SIMULATION 4: Expense Details (Phone Mockup Style)
// ===============================================================================
export function ExpenseDetailsSimulation() {
  const payers = [
    { name: "Elias", avatar: "E", color: "#2d5a3d", amount: "+€90.00" },
    { name: "Maria", avatar: "M", color: "#2d5a3d", amount: "+€60.00" },
  ];

  const splits = [
    { name: "Elias", avatar: "E", color: "#2d5a3d", amount: "€50.00" },
    { name: "Maria", avatar: "M", color: "#5a8f6e", amount: "€50.00" },
    { name: "Dimitroula", avatar: "D", color: "#7ab592", amount: "€50.00" },
  ];

  const netEffects = [
    { name: "Elias", avatar: "E", color: "#2d5a3d", amount: "+€40.00", positive: true },
    { name: "Maria", avatar: "M", color: "#5a8f6e", amount: "+€10.00", positive: true },
    { name: "Dimitroula", avatar: "D", color: "#7ab592", amount: "-€50.00", positive: false },
  ];

  return (
    <div className="sim-phone">
      <div className="sim-phone-notch"></div>
      <div className="sim-phone-screen">
        {/* Header */}
        <div className="sim-screen-header">
          <span className="sim-back-btn">←</span>
          <span className="sim-header-title">Expense Details</span>
          <span className="sim-header-spacer"></span>
        </div>

        {/* Expense Info */}
        <div className="sim-expense-info-phone">
          <div className="sim-expense-title-phone">
            <span className="sim-expense-icon-phone">🍽️</span>
            <div>
              <h4>Group dinner </h4>
              <span className="sim-expense-meta-phone">25 Dec 2024 • Food & Drinks</span>
            </div>
          </div>
          <div className="sim-expense-amount-phone">€150.00</div>
        </div>

        {/* Scrollable Content */}
        <div className="sim-phone-content">
          {/* Paid By Section */}
          <div className="sim-phone-section">
            <h5>💳 PAID BY</h5>
            <div className="sim-phone-list">
              {payers.map((person, idx) => (
                <div key={idx} className="sim-phone-row">
                  <div className="sim-row-left">
                    <div className="sim-avatar-sm" style={{ background: person.color }}>{person.avatar}</div>
                    <span>{person.name}</span>
                  </div>
                  <span className="sim-amount-sm positive">{person.amount}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Split Between Section */}
          <div className="sim-phone-section">
            <h5>📊 SPLIT BETWEEN</h5>
            <div className="sim-phone-list">
              {splits.map((person, idx) => (
                <div key={idx} className="sim-phone-row">
                  <div className="sim-row-left">
                    <div className="sim-avatar-sm" style={{ background: person.color }}>{person.avatar}</div>
                    <span>{person.name}</span>
                  </div>
                  <span className="sim-amount-sm">{person.amount}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Net Effect Section */}
          <div className="sim-phone-section">
            <h5>📈 NET EFFECT</h5>
            <div className="sim-phone-list">
              {netEffects.map((person, idx) => (
                <div key={idx} className="sim-phone-row">
                  <div className="sim-row-left">
                    <div className="sim-avatar-sm" style={{ background: person.color }}>{person.avatar}</div>
                    <span>{person.name}</span>
                  </div>
                  <span className={`sim-amount-sm ${person.positive ? 'positive' : person.neutral ? '' : 'negative'}`}>
                    {person.amount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SIMULATION 5: Settle Up (Phone Mockup Style)
// ===============================================================================
export function SettleUpSimulation() {
  const settlements = [
    { from: "Maria", fromAvatar: "M", fromColor: "#5a8f6e", to: "Elias", toAvatar: "E", toColor: "#2d5a3d", amount: "€45.00" },
    { from: "Dimitroula", fromAvatar: "D", fromColor: "#7ab592", to: "Elias", toAvatar: "E", toColor: "#2d5a3d", amount: "€80.50" },
  ];

  return (
    <div className="sim-phone">
      <div className="sim-phone-notch"></div>
      <div className="sim-phone-screen">
        {/* Header */}
        <div className="sim-screen-header">
          <span className="sim-back-btn">←</span>
          <span className="sim-header-title">Settle Up</span>
          <span className="sim-header-spacer"></span>
        </div>

        {/* Summary Card */}
        <div className="sim-settle-summary-card">
          <span className="sim-settle-icon-lg">💸</span>
          <div>
            <p className="sim-settle-label">Outstanding Balance</p>
            <h3 className="sim-settle-total">€125.50</h3>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="sim-phone-content">
          {/* Suggested Settlements */}
          <div className="sim-phone-section">
            <h5>⏳ SUGGESTED PAYMENTS</h5>
            <div className="sim-settlements-list">
              {settlements.map((s, idx) => (
                <div key={idx} className="sim-settlement-card">
                  <div className="sim-settlement-flow">
                    <div className="sim-settle-person-sm">
                      <div className="sim-avatar-sm" style={{ background: s.fromColor }}>{s.fromAvatar}</div>
                      <span>{s.from}</span>
                    </div>
                    <div className="sim-flow-arrow">
                      <span className="sim-arrow-icon">→</span>
                      <span className="sim-flow-amount">{s.amount}</span>
                    </div>
                    <div className="sim-settle-person-sm">
                      <div className="sim-avatar-sm" style={{ background: s.toColor }}>{s.toAvatar}</div>
                      <span>{s.to}</span>
                    </div>
                  </div>
                  <button className="sim-pay-btn">Mark as Paid</button>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Settlements */}
          <div className="sim-phone-section">
            <h5>✅ RECENT PAYMENTS</h5>
            <div className="sim-phone-list">
              <div className="sim-phone-row completed">
                <div className="sim-row-left">
                  <span className="sim-check-icon">✓</span>
                  <span><b>Maria</b> → <b>Elias</b></span>
                </div>
                <div className="sim-row-right">
                  <span className="sim-amount-sm positive">€65.00</span>
                  <span className="sim-date">18 Dec</span>
                </div>
              </div>
              <div className="sim-phone-row completed">
                <div className="sim-row-left">
                  <span className="sim-check-icon">✓</span>
                  <span><b>Dimitroula</b> → <b>Maria</b></span>
                </div>
                <div className="sim-row-right">
                  <span className="sim-amount-sm positive">€32.00</span>
                  <span className="sim-date">15 Dec</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="sim-phone-banner">
          <span>🎉</span>
          <span>2 payments to settle all debts!</span>
        </div>
      </div>
    </div>
  );
}

export default {
  DashboardSimulation,
  BalancesSimulation,
  GroupsSimulation,
  ExpenseDetailsSimulation,
  SettleUpSimulation
};

